// The top-level sections of the app: ONE list.
//
// Before this file, App.jsx's <Route>s and AppLayout's NAV_ITEMS were two
// hardcoded lists that nothing kept in sync — adding a section meant remembering
// both, and forgetting one produced either a nav link to NotFound or a page
// nobody can navigate to. Routes cannot be generated from here (they carry
// elements, and /support/tickets/new and /support/tickets/:publicRef are routes without sections),
// so the sync is enforced the other way round: App.jsx declares its route paths
// and calls `assertRoutesCoverSections` at module scope, which throws at import
// time — i.e. at boot, in the browser console, loudly — if a section has no route.
//
// The nav itself and the feature-request form's "where" list (`areaLabels`) read the same
// list, for the same reason. The nav is the platform shell's header now: Support's entry
// (support-module/entry.jsx) hands SECTIONS over, and the shell filters it by rank in the
// support product (web/shell/module-logic.js visibleNav), the same question
// `visibleSections` below answers.

import { atLeastRole } from '../session/roles.js';
import { supportPath } from '../support-path.js';

/**
 * `requires: null` means every authenticated user; otherwise `{ product, role }`
 * is the minimum rank, checked with the same ranks the server uses.
 *
 * Admin requires support/lead, matching `requireProductRole('support', 'lead')`
 * on the server router. Drift between the two shows a link whose every call 403s.
 *
 * The same `{ product, role }` shape is what a FEATURE inside the console will
 * carry — the developer's spec gates features on lead vs admin separately — so a
 * section and a descriptor can be asked the same question by `atLeastRole` without
 * this list learning a second vocabulary.
 */
export const SECTIONS = [
  { to: supportPath('/tickets'),   label: 'Tickets',   requires: null },
  { to: supportPath('/customers'), label: 'Customers', requires: null },
  { to: supportPath('/reports'),   label: 'Reports',   requires: null },
  { to: supportPath('/admin'),     label: 'Admin',     requires: { product: 'support', role: 'lead' } },
];

/**
 * The sections to render for this user. Presentation only — the API refuses the
 * rest regardless.
 */
export function visibleSections(user) {
  return SECTIONS.filter(
    (section) => section.requires === null
      || atLeastRole(user, section.requires.product, section.requires.role),
  );
}

/**
 * Where a feature request can say it belongs: every section by label, in nav order, then
 * the two places that are routes without sections ('New ticket', 'Elsewhere'). The third
 * consumer of SECTIONS, so it is derived here rather than retyped: a section added above
 * shows up in the request form's dropdown without anyone editing the dropdown.
 *
 * Takes no user, on purpose. Admin stays in the list for everyone: who may SEE a section
 * is `visibleSections`' question, and anyone may ask for something in one. The label is
 * the stored value, so src/lib/feature-request-vocab.js AREAS (what the server accepts)
 * must say the same; test/web/feature-board.test.js fails until it does.
 *
 * @param {typeof SECTIONS} sections
 * @returns {readonly string[]}  frozen, and a fresh array per call
 */
export function areaLabels(sections = SECTIONS) {
  return Object.freeze([...sections.map((section) => section.label), 'New ticket', 'Elsewhere']);
}

/**
 * Throws if any section has no route. Called at module scope in App.jsx so the
 * failure lands at import time rather than when somebody clicks the link.
 *
 * @param {string[]} routePaths  Every `path` App.jsx renders.
 * @param {typeof SECTIONS} sections
 */
export function assertRoutesCoverSections(routePaths, sections = SECTIONS) {
  const paths = new Set(routePaths);
  const missing = sections.filter((section) => !paths.has(section.to));
  if (missing.length > 0) {
    throw new Error(
      `Nav sections with no route: ${missing.map((s) => s.to).join(', ')}. `
      + 'Add the <Route> in App.jsx, or remove the section from nav-items.js.',
    );
  }
}
